import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  useInfiniteQuery, useQueryClient,
} from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';

import { Formik } from 'formik';
import Card from '../../components/card/Card';
import { AgeGroupListService, AgeGroupUploadService } from '../../services/AuthService';
import HTTPService from '../../services/HTTPService';
import AgeGroupListTable from './AgeGroupListTable';

function CreateAgeGroupsModal({ onClose }) {
  const textColor = useColorModeValue('navy.700', 'white');

  const queryClient = useQueryClient();

  return (
    <Modal isOpen onClose={onClose} isCentered size="3xl">
      <ModalOverlay />
      <Formik
        initialValues={{ min_age: '', max_age: '' }}
        onSubmit={(values, { setFieldError }) => {
          AgeGroupUploadService(values)
            .then((resp) => {
              if (resp.status < 300) {
                queryClient.invalidateQueries({ queryKey: ['age-groups'] });
                queryClient.resetQueries({ queryKey: ['age-groups'] });
                onClose();
              }
            }).catch((uploadError) => {
              if (uploadError.code === 'ERR_BAD_REQUEST') {
                const { data } = uploadError.response;
                Object.entries(data).forEach(([field, errors]) => {
                  setFieldError(field, errors[0]);
                });
              } else {
                setFieldError('non_field_error', 'Unknown error occurred');
              }
              // eslint-disable-next-line no-console
              console.error('Error uploading New Age Group:', uploadError);
            }).finally(() => {

            });
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          errors,
        }) => (
          <form onSubmit={handleSubmit}>

            <ModalContent>
              <ModalHeader>Add New Age Group</ModalHeader>
              <ModalCloseButton />
              <ModalBody
                minH={{ base: 'auto', lg: '420px', '2xl': '365px' }}
                display="flex"
                flexDirection="column"
              >

                <FormControl isInvalid={errors.non_field_errors} pb="24px" isRequired>
                  <FormLabel
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    display="flex"
                  >
                    Minimum Age
                  </FormLabel>
                  <InputGroup size="md">
                    <Input
                      onChange={handleChange}
                      isRequired
                      value={values.min_age}
                      name="min_age"
                      onBlur={handleBlur}
                      fontSize="sm"
                      placeholder="Min Age for Age Group"
                      mb="24px"
                      size="lg"
                      type="text"
                      variant="auth"
                    />
                  </InputGroup>
                  <FormLabel
                    ms="4px"
                    fontSize="sm"
                    fontWeight="500"
                    color={textColor}
                    display="flex"
                  >
                    Maximum Age
                  </FormLabel>
                  <InputGroup size="md">
                    <Input
                      onChange={handleChange}
                      isRequired
                      value={values.max_age}
                      name="max_age"
                      onBlur={handleBlur}
                      fontSize="sm"
                      placeholder="Max Age for Age Group"
                      mb="24px"
                      size="lg"
                      type="text"
                      variant="auth"
                    />
                  </InputGroup>
                  {errors.non_field_errors && <FormErrorMessage>{errors.non_field_errors}</FormErrorMessage>}
                </FormControl>
                <FormControl isInvalid={errors.min_age}>
                  {errors.min_age && (
                  <FormErrorMessage>
                    Min Age:
                    {' '}
                    {errors.min_age}
                  </FormErrorMessage>
                  )}
                </FormControl>
                <FormControl isInvalid={errors.max_age}>
                  {errors.max_age && (
                  <FormErrorMessage>
                    Max Age:
                    {' '}
                    {errors.max_age}
                  </FormErrorMessage>
                  )}
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" me="4px" onClick={onClose}>
                  Close
                </Button>
                <Button colorScheme="green" me="4px" type="submit" disabled={!values.min_age || !values.max_age}>
                  Create
                </Button>
              </ModalFooter>
            </ModalContent>
          </form>

        )}
      </Formik>
    </Modal>
  );
}

export default function AgeGroupsPage() {
  const { ref, inView } = useInView();

  const [isAddAgeGroupsModalOpen, setAddAgeGroupsModalOpen] = useState(false);

  const {
    data,
    isFetching,
    fetchNextPage,
  } = useInfiniteQuery(
    ['age-groups'],
    async ({ pageParam = 0 }) => {
      const res = await (pageParam === 0 ? AgeGroupListService() : HTTPService.get(pageParam));
      return res.data;
    },
    {
      getPreviousPageParam: (firstPage) => firstPage.previous ?? undefined,
      getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    },
  );

  const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
  const textColorBrand = useColorModeValue('brand.500', 'white');

  useEffect(() => {
    if (inView && !isFetching) {
      fetchNextPage();
    }
  }, [inView, isFetching]);

  return (
    <>
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Flex direction="column">
          <Flex
            mt="45px"
            mb="20px"
            justifyContent="flex-end"
            direction={{ base: 'column', md: 'row' }}
            align={{ base: 'start', md: 'center' }}
          >
            <Flex
              align="center"
              ms={{ base: '24px', md: '0px' }}
              mt={{ base: '20px', md: '0px' }}
            >
              <Button
                color={textColorBrand}
                fontWeight="500"
                onClick={() => setAddAgeGroupsModalOpen(true)}
              >
                + Add Plan
              </Button>

            </Flex>
          </Flex>
        </Flex>
        <Grid
          gap={{ base: '20px', xl: '20px' }}
        >
          <Card mb={{ base: '0px', '2xl': '20px' }}>
            <Text
              color={textColorPrimary}
              fontWeight="bold"
              fontSize="2xl"
              mt="10px"
              mb="4px"
            >
              All Age Groups
            </Text>
            <AgeGroupListTable
              columnsData={[
                {
                  Header: 'MIN AGE',
                  accessor: 'min_age',
                },
                {
                  Header: 'MAX AGE',
                  accessor: 'max_age',
                },
                {
                  Header: 'EDIT',
                  accessor: 'edit',
                },
              ]}
              tableData={data ? data.pages.reduce((previous, item) => previous.concat(item.results), []) : []}
            />
            <div ref={ref} />
          </Card>
        </Grid>
      </Box>
      {isAddAgeGroupsModalOpen && <CreateAgeGroupsModal onClose={() => setAddAgeGroupsModalOpen(false)} />}
    </>
  );
}
